<!--
    HOC (Higher-Order Component) that dispatches to the correct tile layout
    based on the `tileLayout` prop. Add new layout variants here.
-->
<script lang="ts">
    import type { SearchContext, Variation } from '@shared/types/types.ts';
    import type { ImageAspectRatio } from '@tile/components/product-image/types.ts';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import type { TileLayout as TileLayoutSelector } from '@tilelist/domain/composition/BracketLayout.ts';
    import { shouldShowAvailability } from '@tilelist/domain/feature-visibility/AvailabilityVisibility.ts';
    import { shouldShowRating } from '@tilelist/domain/feature-visibility/RatingVisibility.ts';
    import DefaultLayout from '@tilelist/layouts/default-layout/DefaultLayout.svelte';
    import { createDefaultLayout } from '@tilelist/layouts/default-layout/DefaultLayout.ts';
    import FashionLayout from '@tilelist/layouts/fashion-layout/FashionLayout.svelte';
    import { createFashionLayout } from '@tilelist/layouts/fashion-layout/FashionLayout.ts';
    import type { TileLayoutProps } from '@tilelist/layouts/shared/TileLayoutProps.ts';
    import TileLayout from '@tilelist/layouts/tile-layout/TileLayout.svelte';
    import { createTileLayout } from '@tilelist/layouts/tile-layout/TileLayout.ts';

    type Props = TileLayoutProps & {
        tileLayout: TileLayoutSelector;
        variation: Variation;
        aspectRatio: ImageAspectRatio;
        showTopInfos: boolean;
        showInstallments: boolean;
        showMultipleSizes: boolean;
        searchContext?: SearchContext;
    };

    let {
        tileLayout,
        variation,
        aspectRatio,
        showTopInfos,
        showInstallments,
        showMultipleSizes,
        searchContext,
        ...coreProps
    }: Props = $props();

    const onex = $derived(injectWiring().onex);
    const emphasizeTopInfos = $derived(onex.isExperimentGroup('TOP_INFO_SCANABILITY', 'E3946B'));

    /**
 *
 *
 */
    const useTileLayout = $derived(onex.isToggleActive('REPTILE_1738_THE_ONE_LAYOUT'));
</script>

{#if useTileLayout}
    <!--
        NOTE (deferred refactor): unlike showInstallments/showMultipleSizes, showRating/showAvailability
        are computed inline here from searchContext instead of in ProductGrid.svelte, because
        ProductGrid.svelte/ProductTileItem.svelte are still shared with the toggle-OFF StatusQuo path.
        Align once that path is removed (section 7 cleanup).
    -->
    <TileLayout
        {...coreProps}
        layout={createTileLayout(variation, showTopInfos, showMultipleSizes, searchContext)}
        {aspectRatio}
        {showInstallments}
        {emphasizeTopInfos}
        showRating={shouldShowRating(searchContext)}
        showAvailability={shouldShowAvailability(searchContext)}
    />
{:else if tileLayout === 'fashion'}
    <FashionLayout {...coreProps} layout={createFashionLayout(variation)} {aspectRatio} />
{:else}
    <DefaultLayout
        {...coreProps}
        layout={createDefaultLayout(variation, showTopInfos, showMultipleSizes)}
        {aspectRatio}
        {showInstallments}
        {emphasizeTopInfos}
    />
{/if}
